import { CheckCircle, Map } from '@mui/icons-material';
import {
	Column,
	ColumnFindAsset,
	ColumnNoCD,
	ColumnNoCDFindAsset,
	headerReactNodes,
} from '@Reducers/riskDetailsPagination';
import {
	AssetType,
	ColumnFilterValue,
	Sort2 as Sort,
} from '@innovyze/lib_am_common';
import { getAssetLocation } from '@innovyze/lib_am_common/Actions';
import { makeMapURL } from '@innovyze/lib_am_common/Utils';
import {
	useSelectAssetLocation,
	useSelectAssetLocationIsWaiting,
} from '@innovyze/lib_am_common/Selectors';
import {
	DataGridButton,
	DataGridButtonType,
	ExportButtonWithDialog,
	MenuListButton,
	StatusTag,
	StylovyzeTable,
	StylovyzeTableRow,
	getSubscriptions,
	rem,
	riskGradePalette,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import { Grid, Link } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
	changeFilterSelect,
	changePage,
	changeSearch,
	changeSlice,
	changeSort,
	reloadRiskDetails,
	setRunCompleteReload,
	setTab,
} from '@Actions';
import {
	selectCanReload,
	selectRiskConfig,
	selectRiskDetailsFilterProps,
	selectRiskDetailsIsLoading,
	selectRiskDetailsIsWaiting,
	selectRiskDetailsPagination,
	selectRiskDetailsQuery,
	selectRiskDetailsSearch,
	selectRiskDetailsStatus,
	selectRiskDetailsTablePage,
	selectRowsPerPage,
	selectRunCompleteReload,
} from '@Selectors/Risk.selectors';
import { useGlobalization } from '@Translations/useGlobalization';

import { FilterSelect } from '@innovyze/lib_am_common/Components';
import { StyledGrid } from '@Utils/styles';
import { addRouteNamespace } from '@Utils/actions';
import { fullAccess } from '@innovyze/shared-utils';
import { parse } from 'query-string';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { translatedFilters } from '@Utils/translations';
import { NoReasultsRisk } from './NoResultsRisk.component';

interface Props {
	configId: string;
}

export const noConditionData = (assetType: string) => {
	// For the moment only Water Distibuion Pipe has no Condition Data but this will expand
	return assetType === 'pipe';
};

export interface AssetIdAndType {
	assetId: string;
	assetType: AssetType;
}

export const RiskDetails = ({ configId }: Props) => {
	const { t } = useGlobalization();
	const history = useHistory();
	const dispatch = useDispatch();
	const subscriptions = getSubscriptions();
	const hasFullAccess = fullAccess(subscriptions);

	const showFindAsset = useIsFeatureEnabled('info-360-navigate-to-asset')
		? true
		: false;

	const riskConfig = selectRiskConfig();

	const canReload = selectCanReload();
	const runCompleteReload = selectRunCompleteReload();

	const filters = selectRiskDetailsFilterProps();
	const isLoading = selectRiskDetailsIsLoading();
	const isWaiting = selectRiskDetailsIsWaiting();
	const status = selectRiskDetailsStatus();

	const queryString = selectRiskDetailsQuery();
	const tablePage = selectRiskDetailsTablePage();
	const pagination = selectRiskDetailsPagination();
	const requestParams = {
		...parse(queryString),
		slice: '100000',
		page: '1',
	};

	const assetLocation = useSelectAssetLocation();
	const isWaitingAssetLocation = useSelectAssetLocationIsWaiting();
	const [findAsset, setFindAsset] = useState<AssetIdAndType | undefined>(
		undefined,
	);

	useEffect(() => {
		dispatch(setTab('details'));
	}, []);

	useEffect(() => {
		if (canReload && runCompleteReload) {
			refreshList();
			setRunCompleteReload(false);
		}
	}, [runCompleteReload]);

	const dispatchSliceFn = (slice: number) => dispatch(changeSlice(slice));
	const dispatchSearchFn = (text: string) => dispatch(changeSearch(text));

	const dispatchSortFn = (
		_e: object,
		name: React.ReactText,
		order: string,
	) => {
		const sortPayload: Sort = {
			key: name.toString(),
			descending: order !== 'asc',
		};
		dispatch(changeSort(sortPayload));
	};

	const statusCell = (status: string) => {
		switch (status) {
			case '0':
				return (
					<StatusTag
						text={t('Negligible')}
						color="negligible"
						width={rem(92)}
					/>
				);
			case '1':
				return (
					<StatusTag text={t('Low')} color="low" width={rem(92)} />
				);
			case '2':
				return (
					<StatusTag
						text={t('Medium')}
						color="medium"
						width={rem(92)}
					/>
				);
			case '3':
				return (
					<StatusTag text={t('High')} color="high" width={rem(92)} />
				);
			case '4':
				return (
					<StatusTag
						text={t('Extreme')}
						color="extreme"
						width={rem(92)}
					/>
				);
			default:
				return <></>;
		}
	};

	const trendCell = (trend: string) => {
		const value = parseFloat(trend);
		if (isNaN(value)) {
			return trend;
		} else {
			if (parseFloat(trend) < 0)
				return <b style={{ color: 'green' }}>{trend}</b>;
			else if (parseFloat(trend) > 0)
				return <b style={{ color: 'red' }}>+{trend}</b>;
			else return trend;
		}
	};

	const conditionCell = (condition: string) => {
		if (condition === '1' || condition === 'true') {
			return (
				<CheckCircle
					style={{
						color: riskGradePalette.low.dark,
						verticalAlign: 'middle',
					}}
				/>
			);
		} else {
			return <></>;
		}
	};

	const menuCell = (assetId: string) => {
		return (
			<div key={'findAsset'} role="button" tabIndex={0}>
				<MenuListButton
					menuItems={[
						{
							text: t('Find Asset in Network'),
							icon: <Map />,
							onClick: () => {
								const mapAsset = {
									assetId,
									assetType: riskConfig.riskSetting
										.assetType as AssetType,
								};
								setFindAsset(mapAsset);

								dispatch(getAssetLocation(mapAsset));
							},
						},
					]}
					buttonCy="actions"
				/>
			</div>
		);
	};

	useEffect(() => {
		if (
			findAsset &&
			assetLocation &&
			assetLocation.assetId === findAsset.assetId
		) {
			const url = makeMapURL(
				findAsset.assetType,
				findAsset.assetId,
				assetLocation.position,
			);

			if (url) history.push(url);

			setFindAsset(undefined);
		}
	}, [assetLocation, findAsset]);

	const rowReactNodes = (row: string[]): React.ReactNode[] => {
		if (showFindAsset) {
			if (noConditionData(riskConfig.riskSetting.assetType)) {
				return row.map((cell, nCol) => {
					switch (nCol) {
						case ColumnNoCDFindAsset.AssetId:
							return (
								<Link
									underline="hover"
									style={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										cursor: 'pointer',
									}}
									title={cell}
									onClick={() => {
										history.push(
											addRouteNamespace(
												`riskAssetDetails/${cell}`,
											),
										);
									}}>
									{cell}
								</Link>
							);
						case ColumnNoCDFindAsset.RiskTrend:
							return trendCell(cell);
						case ColumnNoCDFindAsset.Grade:
							return statusCell(cell?.toString());
						case ColumnNoCDFindAsset.Menu:
							return menuCell(row[Column.AssetId]);
						default:
							return cell;
					}
				});
			} else {
				return row.map((cell, nCol) => {
					switch (nCol) {
						case ColumnFindAsset.AssetId:
							return (
								<Link
									underline="hover"
									style={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										cursor: 'pointer',
									}}
									title={cell}
									onClick={() => {
										history.push(
											addRouteNamespace(
												`riskAssetDetails/${cell}`,
											),
										);
									}}>
									{cell}
								</Link>
							);
						case ColumnFindAsset.RiskTrend:
							return trendCell(cell);
						case ColumnFindAsset.ConditionData:
							return conditionCell(cell?.toString());
						case ColumnFindAsset.Grade:
							return statusCell(cell?.toString());
						case ColumnFindAsset.Menu:
							return menuCell(row[Column.AssetId]);
						default:
							return cell;
					}
				});
			}
		} else {
			if (noConditionData(riskConfig.riskSetting.assetType)) {
				return row.map((cell, nCol) => {
					switch (nCol) {
						case ColumnNoCD.AssetId:
							return (
								<Link
									underline="hover"
									style={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										cursor: 'pointer',
									}}
									title={cell}
									onClick={() => {
										history.push(
											addRouteNamespace(
												`riskAssetDetails/${cell}`,
											),
										);
									}}>
									{cell}
								</Link>
							);
						case ColumnNoCD.RiskTrend:
							return trendCell(cell);
						case ColumnNoCD.Grade:
							return statusCell(cell?.toString());
						default:
							return cell;
					}
				});
			} else {
				return row.map((cell, nCol) => {
					switch (nCol) {
						case Column.AssetId:
							return (
								<Link
									underline="hover"
									style={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										cursor: 'pointer',
									}}
									title={cell}
									onClick={() => {
										history.push(
											addRouteNamespace(
												`riskAssetDetails/${cell}`,
											),
										);
									}}>
									{cell}
								</Link>
							);
						case Column.RiskTrend:
							return trendCell(cell);
						case Column.ConditionData:
							return conditionCell(cell?.toString());
						case Column.Grade:
							return statusCell(cell?.toString());
						default:
							return cell;
					}
				});
			}
		}
	};

	const rows: StylovyzeTableRow[] =
		tablePage.cells.map((row: string[], nRow: number) => ({
			key: nRow,
			cells: rowReactNodes(row),
		})) || [];

	const filterChange = (value: ColumnFilterValue) => {
		dispatch(changeFilterSelect(value));
	};

	const refreshList = () => {
		dispatch(changePage(tablePage.page));
	};

	const getTitleArea = () => {
		return (
			<StyledGrid alignItems="center" container style={{ width: '70%' }}>
				<Grid
					container
					item
					justifyContent="space-evenly"
					alignItems="center"
					data-cy="grid-filters"
					xs={6}>
					{translatedFilters(filters, t).map((filterProps, index) => (
						<Grid
							item
							alignItems="center"
							key={index}
							xs={6}
							style={{
								paddingLeft: '20px',
								paddingRight: '20px',
							}}>
							<FilterSelect
								{...filterProps}
								key={filterProps.column.id}
								onFilterChange={filterChange}
								fullWidth={true}
							/>
						</Grid>
					))}
				</Grid>
				<Grid
					item
					xs={6}
					container
					justifyContent="flex-end"
					data-cy="grid-actionbutton">
					<DataGridButton
						type={DataGridButtonType.REFRESH}
						disabled={isLoading || isWaiting}
						data-cy="refreshButton"
						onClick={refreshList}
					/>
					{hasFullAccess && (
						<ExportButtonWithDialog
							total={tablePage.total}
							requestParams={
								requestParams as Record<string, string>
							}
							disabled={isLoading || isWaiting}
							source={'risk.export.create'}
							title={t('Export')}
							maxRows={100000}
							dialogDataCy="export-dialog"
							iconDataCy="export-icon"
							maxPollAttempts={60}
							iterationTime={5000}
						/>
					)}
				</Grid>
			</StyledGrid>
		);
	};

	return (
		<StylovyzeTable
			className={
				isLoading || isWaiting || isWaitingAssetLocation
					? 'ScrollTable wait'
					: 'ScrollTable'
			}
			dataCy="risk-details-table"
			titleArea={getTitleArea()}
			headers={headerReactNodes(
				riskConfig?.riskSetting.assetType,
				showFindAsset,
				t,
			)}
			search={{
				onChange: query => {
					dispatchSearchFn(query);
					dispatch(reloadRiskDetails(configId));
				},
				onRequestSearch: () => {
					dispatch(reloadRiskDetails(configId));
				},
				onCancelSearch: () => {
					dispatchSearchFn('');
					dispatch(reloadRiskDetails(configId));
				},
				placeholder: t('Search'),
				value: selectRiskDetailsSearch(),
				dataCy: 'search-01',
			}}
			sort={{
				name: tablePage.sortKey,
				order: tablePage.sortDescending ? 'desc' : 'asc',
				onRequestSort: (_e, name, order) => {
					dispatchSortFn(_e, name, order);
				},
			}}
			rows={rows}
			rowsPerPage={selectRowsPerPage(dispatchSliceFn)}
			rowHeight={rem(40)}
			pagination={{
				totalNumberOfPages: tablePage.pages,
				dataCy: 'pagination-01',
				currentPage: tablePage.page,
				onChange: (_e, page) => {
					dispatch(changePage(page));
				},
				extraInfo: t('{{from}}-{{to}} of {{total}}', {
					from: (pagination.page - 1) * pagination.slice + 1,
					to:
						pagination.page == pagination.pages
							? pagination.total
							: pagination.page * pagination.slice,
					total: pagination.total,
				}),
			}}
			initialized={!isLoading && status.code !== 800}
			useSkeleton={{ rowCount: 10 }}
			emptyTableBody={
				<NoReasultsRisk
					adminFullAccess={hasFullAccess}
					configId={configId}
				/>
			}
		/>
	);
};
